import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Input,
  CircularProgress,
  Button,
  Sheet,
  Select,
  Option,
  Textarea,
  Chip,
} from '@mui/joy';
import { ChevronLeft, CircleDot, Clock3, CheckCircle2 } from 'lucide-react';
import { authFetch } from '../../utils/authFetch';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function DettaglioIssue() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [initialSubject, setInitialSubject] = useState('');
  const [initialDescription, setInitialDescription] = useState('');
  const [initialResolution, setInitialResolution] = useState('');
  const [initialState, setInitialState] = useState('open');

  const selectedOrg = localStorage.getItem('selectedOrg') || '1';

  const stateLabels = {
    open: {
      label: 'Aperta',
      color: 'danger',
      icon: <CircleDot size={16} />,
    },
    in_progress: {
      label: 'In lavorazione',
      color: 'warning',
      icon: <Clock3 size={16} />,
    },
    closed: {
      label: 'Chiusa',
      color: 'success',
      icon: <CheckCircle2 size={16} />,
    },
  };

  useEffect(() => {
    const fetchIssue = async () => {
      setLoading(true);
      try {
        const res = await authFetch(
          `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/issue/${id}`,
          { credentials: 'include' }
        );

        if (!res.ok) throw new Error('Errore fetch issue');

        const data = await res.json();

        setIssue({
          ...data,
          subject: data.subject || '',
          description: data.description || '',
          resolution: data.resolution || '',
          state: data.state || 'open',
        });

        setInitialSubject(data.subject || '');
        setInitialDescription(data.description || '');
        setInitialResolution(data.resolution || '');
        setInitialState(data.state || 'open');
      } catch (err) {
        console.error('Errore nel fetch issue:', err);
        toast.error('Errore nel caricamento della issue', {
          position: 'top-right',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchIssue();
  }, [selectedOrg, id]);

  const handleSalva = async () => {
    if (!issue) return;

    setSaving(true);

    const body = {
      subject: issue.subject,
      description: issue.description,
      resolution: issue.resolution,
      state: issue.state,
    };

    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/issue/${id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body),
        }
      );

      if (!res.ok) throw new Error('Errore nel salvataggio issue');

      const updated = await res.json();

      setIssue({
        ...updated,
        subject: updated.subject || '',
        description: updated.description || '',
        resolution: updated.resolution || '',
        state: updated.state || 'open',
      });

      setInitialSubject(updated.subject || '');
      setInitialDescription(updated.description || '');
      setInitialResolution(updated.resolution || '');
      setInitialState(updated.state || 'open');

      toast.success('Modifiche salvate con successo', {
        position: 'top-right',
        autoClose: 3000,
      });
    } catch (err) {
      console.error('Errore salvataggio issue:', err);
      toast.error('Errore nel salvataggio', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChiudiIssue = async () => {
    if (!issue) return;

    const userId = issue.closed_by || issue.created_by;

    setSaving(true);

    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/issue/${id}/close`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            closed_by: userId,
            resolution: issue.resolution || null,
          }),
        }
      );

      if (!res.ok) throw new Error('Errore nella chiusura issue');

      const updated = await res.json();

      setIssue({
        ...updated,
        subject: updated.subject || '',
        description: updated.description || '',
        resolution: updated.resolution || '',
        state: updated.state || 'closed',
      });

      setInitialSubject(updated.subject || '');
      setInitialDescription(updated.description || '');
      setInitialResolution(updated.resolution || '');
      setInitialState(updated.state || 'closed');

      toast.success('Issue chiusa con successo', {
        position: 'top-right',
        autoClose: 3000,
      });
    } catch (err) {
      console.error('Errore chiusura issue:', err);
      toast.error('Errore nella chiusura della issue', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return isNaN(date)
      ? ''
      : `${date.toLocaleDateString('it-IT')} ${date.toLocaleTimeString('it-IT', {
          hour: '2-digit',
          minute: '2-digit',
        })}`;
  };

  const isModified =
    issue &&
    (
      issue.subject !== initialSubject ||
      issue.description !== initialDescription ||
      issue.resolution !== initialResolution ||
      issue.state !== initialState
    );

  const stateInfo = issue ? stateLabels[issue.state] : null;

  return (
    <Box>
      <Typography level="h3" mb={1}>
        Dettaglio Issue
      </Typography>

      <Button
        startDecorator={<ChevronLeft />}
        onClick={() => navigate(-1)}
        variant="outlined"
        size="sm"
        sx={{ mb: 3 }}
      >
        Indietro
      </Button>

      {loading ? (
        <CircularProgress />
      ) : !issue ? (
        <Typography color="danger">Issue non trovata.</Typography>
      ) : (
        <Sheet
          variant="outlined"
          sx={{
            p: 3,
            borderRadius: 'md',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            minWidth: 280,
          }}
        >
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            {stateInfo && (
              <Chip
                variant="soft"
                color={stateInfo.color}
                startDecorator={stateInfo.icon}
                size="sm"
              >
                {stateInfo.label}
              </Chip>
            )}

            <Typography level="body-sm" color="neutral">
              Mezzo: <b>{issue.vehicle_name || '—'}</b>
            </Typography>
          </Box>

          <Input
            value={issue.subject || ''}
            placeholder="Oggetto"
            startDecorator={<Typography level="body-sm">Oggetto</Typography>}
            onChange={(e) =>
              setIssue((prev) => ({ ...prev, subject: e.target.value }))
            }
          />

          <Textarea
            minRows={4}
            value={issue.description || ''}
            placeholder="Descrizione"
            onChange={(e) =>
              setIssue((prev) => ({ ...prev, description: e.target.value }))
            }
          />

          <Textarea
            minRows={4}
            value={issue.resolution || ''}
            placeholder="Risoluzione"
            onChange={(e) =>
              setIssue((prev) => ({ ...prev, resolution: e.target.value }))
            }
          />

          <Select
            value={issue.state || 'open'}
            onChange={(_, newValue) =>
              setIssue((prev) => ({ ...prev, state: newValue || 'open' }))
            }
            startDecorator={<Typography level="body-sm">Stato</Typography>}
          >
            <Option value="open">Aperta</Option>
            <Option value="in_progress">In lavorazione</Option>
            <Option value="closed">Chiusa</Option>
          </Select>

          <Input
            disabled
            value={issue.created_by_name || ''}
            startDecorator={<Typography level="body-sm">Creata da</Typography>}
          />

          <Input
            disabled
            value={issue.closed_by_name || ''}
            startDecorator={<Typography level="body-sm">Chiusa da</Typography>}
          />

          <Input
            disabled
            value={formatDate(issue.created_at)}
            startDecorator={<Typography level="body-sm">Creata il</Typography>}
          />

          <Input
            disabled
            value={formatDate(issue.updated_at)}
            startDecorator={<Typography level="body-sm">Aggiornata il</Typography>}
          />

          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            <Button
              size="sm"
              onClick={handleSalva}
              loading={saving}
              disabled={saving || !isModified}
              color="primary"
            >
              Salva
            </Button>

            {issue.state !== 'closed' && (
              <Button
                size="sm"
                variant="solid"
                color="success"
                onClick={handleChiudiIssue}
                disabled={saving}
              >
                Chiudi Problema
              </Button>
            )}
          </Box>
        </Sheet>
      )}

      <ToastContainer />
    </Box>
  );
}